import css from '@emotion/css'
import { InfoLabel, isEmpty, Text, VFlow } from 'bold-ui'
import { DateTime } from 'components/date'
import { useFlags } from 'config/useFlagsContext'
import {
  AtendimentoProfissionalPreNatal,
  AtendimentoProfissionalPuericultura,
  EvolucaoObjetivo,
  HistoricoObjetivoPecFragmentFragment,
  MarcadorConsumoAlimentar,
  ResultadoExame,
} from 'graphql/types.generated'
import React, { Fragment } from 'react'
import { isUndefinedOrNull } from 'util/checks'
import { calculateAge } from 'util/date/calculateAge'
import { HistoricoSectionStyles } from 'view/atendimentos/detail/historico/model/model-historico'
import { filterByRegistroAnterior } from 'view/atendimentos/detail/historico/utils/filterByRegistroAnterior'
import {
  convertMedicoesToHistoricoMedicaoModel,
  getMedicoesPreNatal,
  hasAllAttrsNull,
  hasMedicoes,
  naoRegistradoNesseAtendimento,
} from 'view/atendimentos/detail/historico/utils/utils-historico'
import { ArquivoAtendimentoProfissionalHistorico } from 'view/atendimentos/detail/soap/objetivo/anexo-arquivos/model-anexoArquivos'
import { IVCF_RESULTADOS } from 'view/atendimentos/detail/soap/objetivo/ivcf/model-ivcf'
import { HistoricoMedicaoModel } from 'view/atendimentos/types/model-historicoMedicao'

import HistoricoArquivosAnexadosTableBox from '../../HistoricoArquivosAnexadosTableBox'
import { HistoricoIvcfTableBox } from '../../HistoricoIvcfTableBox'
import HistoricoMCATableBox from '../../HistoricoMCATableBox'
import HistoricoResultadoExamesTable from '../../HistoricoResultadoExamesTable'
import HistoricoMedicoesPanel from '../../medicoes/HistoricoMedicoesPanel'
import HistoricoMedicoesPreNatalTableBox from '../pre-natal/HistoricoMedicoesPreNatalTableBox'
import HistoricoMChatTableBox from '../puericultura/HistoricoMChatTableBox'
import HistoricoObjetivoInformacoesPuericultura from '../puericultura/HistoricoObjetivoInformacoesPuericultura'

type Ivcf = HistoricoObjetivoPecFragmentFragment['ivcf']

export type MChat = HistoricoObjetivoPecFragmentFragment['mChat']

export type AtendimentoProfissionalPreNatalModel = Pick<
  AtendimentoProfissionalPreNatal,
  'id' | 'gravidezPlanejada' | 'movimentacaoFetal' | 'tipoEdema'
>

export interface HistoricoObjetivoPecPanelProps {
  evolucaoObjetivo: EvolucaoObjetivo
  medicoes: HistoricoMedicaoModel[]
  isAtendObservacao?: boolean
  examesResultados?: ResultadoExame[]
  atendProfPreNatal?: AtendimentoProfissionalPreNatalModel
  atendProfPuericultura?: AtendimentoProfissionalPuericultura
  marcadorConsumoAlimentar?: MarcadorConsumoAlimentar
  ivcf?: Ivcf
  dataNascimentoCidadao?: LocalDate
  dataAtendimento?: LocalDate
  arquivosAnexados?: ArquivoAtendimentoProfissionalHistorico[]
  mChat?: MChat
}

export default function HistoricoObjetivoPecPanel(props: HistoricoObjetivoPecPanelProps) {
  const {
    evolucaoObjetivo,
    medicoes,
    examesResultados,
    atendProfPreNatal,
    atendProfPuericultura,
    marcadorConsumoAlimentar,
    isAtendObservacao,
    ivcf,
    dataNascimentoCidadao,
    dataAtendimento,
    arquivosAnexados,
    mChat,
  } = props

  const { PILOTO_ANEXO_ARQUIVOS_ENABLED, M_CHAT_ENABLED } = useFlags()

  const { recentes: medicoesRecentes } = filterByRegistroAnterior<HistoricoMedicaoModel>(medicoes, 'medicaoAnterior')

  const descricao = evolucaoObjetivo?.descricao?.removeTags()

  const possuiInformacoesPuericultura: boolean = atendProfPuericultura && !hasAllAttrsNull(atendProfPuericultura)

  const hasAtendProfPreNatal: boolean =
    atendProfPreNatal?.gravidezPlanejada || atendProfPreNatal?.movimentacaoFetal || !!atendProfPreNatal?.tipoEdema

  const preNatalInfo = getMedicoesPreNatal(medicoesRecentes)

  const hasPreNatalInfo = hasAtendProfPreNatal || !isEmpty(preNatalInfo)

  const shouldDisplayMCA = !!marcadorConsumoAlimentar
  const shouldDisplayArquivosAnexados = !!(PILOTO_ANEXO_ARQUIVOS_ENABLED && arquivosAnexados?.isNotEmpty())
  const shouldDisplayMChat = M_CHAT_ENABLED && mChat

  const hasAgeDates = !isUndefinedOrNull(dataNascimentoCidadao) && !isUndefinedOrNull(dataAtendimento)
  const ageInMonths = hasAgeDates ? calculateAge(dataNascimentoCidadao, dataAtendimento).ageInMonths : null

  const hasObjetivo: boolean =
    !!descricao ||
    !isEmpty(examesResultados) ||
    hasMedicoes(medicoesRecentes) ||
    hasPreNatalInfo ||
    possuiInformacoesPuericultura ||
    shouldDisplayMCA ||
    !!ivcf ||
    shouldDisplayArquivosAnexados ||
    !!mChat

  const sortedItems: Array<HistoricoMedicaoModel> = (medicoesRecentes || [])
    .sort((a, b) => a.dataMedicao - b.dataMedicao)
    .reverse()
  const { dum, vacinacaoEmDia } = resolveDumAndVacinacaoEmDia(sortedItems)

  const styles = createStyles(HistoricoSectionStyles['O'].color)

  return (
    <Fragment>
      {hasObjetivo ? (
        <VFlow>
          {descricao && <Text dangerouslySetInnerHTML={{ __html: evolucaoObjetivo.descricao }} />}

          {shouldDisplayArquivosAnexados && <HistoricoArquivosAnexadosTableBox arquivosAnexados={arquivosAnexados} />}

          {dum && (
            <InfoLabel title='DUM' titleStyles={styles.color}>
              {<DateTime value={dum} format='DD/MM/YYYY' />}
            </InfoLabel>
          )}

          {hasMedicoes(medicoesRecentes) && (
            <HistoricoMedicoesPanel
              sectionStyle='O'
              medicoes={medicoesRecentes}
              isAtendimentoObservacao={isAtendObservacao}
            />
          )}

          {shouldDisplayMCA && <HistoricoMCATableBox marcadorConsumoAlimentar={marcadorConsumoAlimentar} />}

          {vacinacaoEmDia && (
            <InfoLabel title='Vacinação em dia' titleStyles={styles.color}>
              Sim
            </InfoLabel>
          )}

          {ivcf && (
            <HistoricoIvcfTableBox
              ivcf={ivcf}
              riscoVulnerabilidadeText={IVCF_RESULTADOS[ivcf.riscoVulnerabilidade].longTitle}
            />
          )}

          {hasPreNatalInfo && (
            <HistoricoMedicoesPreNatalTableBox medicoes={preNatalInfo} atendProfPreNatal={atendProfPreNatal} />
          )}
          {possuiInformacoesPuericultura && (
            <HistoricoObjetivoInformacoesPuericultura
              medicoes={convertMedicoesToHistoricoMedicaoModel(medicoesRecentes)}
              atendProfPuericultura={atendProfPuericultura}
            />
          )}
          {shouldDisplayMChat && <HistoricoMChatTableBox mChat={mChat} idadeEmMeses={ageInMonths} />}
          {!isEmpty(examesResultados) && <HistoricoResultadoExamesTable examesResultados={examesResultados} />}
        </VFlow>
      ) : (
        <Text fontStyle='italic'>{naoRegistradoNesseAtendimento}</Text>
      )}
    </Fragment>
  )
}

const resolveDum = (items: HistoricoMedicaoModel[]) => {
  if (items.isEmpty()) return null

  const dums = items.map((item) => item.dum)

  return dums.find((dum) => !isUndefinedOrNull(dum))
}

const resolveVacinacaoEmDia = (items: HistoricoMedicaoModel[]) => {
  if (items.isEmpty()) return null

  const values = items.map((item) => item.valorVacinacaoEmDia)

  return values.find((vacinacaoEmDia) => !isUndefinedOrNull(vacinacaoEmDia))
}

const resolveDumAndVacinacaoEmDia = (items: HistoricoMedicaoModel[]) => {
  const dum = resolveDum(items)
  const vacinacaoEmDia = resolveVacinacaoEmDia(items)

  return { dum, vacinacaoEmDia }
}

const createStyles = (cor: string) => ({
  color: css`
    color: ${cor};
  `,
})
